import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { engagementsApi } from '../../api/engagements.js';
import { speakersApi } from '../../api/speakers.js';
import { eventsApi } from '../../api/events.js';
import { SearchBar } from '../../components/common/SearchBar.jsx';
import { StatusBadge } from '../../components/common/StatusBadge.jsx';
import { LoadingSpinner } from '../../components/common/LoadingSpinner.jsx';
import { EmptyState } from '../../components/common/EmptyState.jsx';
import { ErrorAlert } from '../../components/common/ErrorAlert.jsx';
import { formatDate } from '../../utils/dates.js';
import { usePageHeader } from '../../hooks/usePageHeader.jsx';
import { ENGAGEMENT_STATUSES } from '../../utils/constants.js';

export function EngagementListPage() {
  usePageHeader('Engagements');
  const [searchParams] = useSearchParams();
  const [engagements, setEngagements] = useState([]);
  const [speakers, setSpeakers] = useState({});
  const [events, setEvents] = useState({});
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') ?? '');
  const [dueState, setDueState] = useState(searchParams.get('dueState') ?? '');
  const [pendingReview, setPendingReview] = useState(searchParams.get('pendingReview') === 'true');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = {};
    if (status) params.status = status;
    if (dueState) params.dueState = dueState;
    if (pendingReview) params.pendingReview = 'true';

    Promise.all([
      engagementsApi.list(params),
      speakersApi.list(),
      eventsApi.list(),
    ])
      .then(([eng, spks, evts]) => {
        setEngagements(eng ?? []);
        setSpeakers(Object.fromEntries((spks ?? []).map((s) => [s.id, s])));
        setEvents(Object.fromEntries((evts ?? []).map((e) => [e.id, e])));
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [status, dueState, pendingReview]);

  const today = new Date().toISOString().slice(0, 10);

  const filtered = engagements.filter((e) => {
    if (!q) return true;
    const sp = speakers[e.speakerId];
    return (
      e.talkTitle?.toLowerCase().includes(q.toLowerCase()) ||
      `${sp?.firstName} ${sp?.lastName}`.toLowerCase().includes(q.toLowerCase())
    );
  });

  return (
    <div>
      <div className="page-toolbar">
        <SearchBar value={q} onChange={setQ} placeholder="Search engagements…" />
        <div className="inline">
          <select className="form-select" style={{ width: 'auto' }} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            <option value="">All statuses</option>
            {ENGAGEMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="form-select" style={{ width: 'auto' }} value={dueState} onChange={(e) => setDueState(e.target.value)} aria-label="Due state">
            <option value="">Any due date</option>
            <option value="overdue">Overdue</option>
            <option value="today">Due today</option>
            <option value="soon">Due in 14 days</option>
          </select>
          <label className="inline" style={{ fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
            <input type="checkbox" checked={pendingReview} onChange={(e) => setPendingReview(e.target.checked)} />
            Needs review
          </label>
          <Link to="/engagements/new" className="btn btn-primary">+ New</Link>
        </div>
      </div>
      <div className="page-body">
        <ErrorAlert error={error} />
        {loading ? <LoadingSpinner /> : filtered.length === 0 ? (
          <EmptyState title="No engagements" description="Create an engagement to start tracking a speaker." action={<Link to="/engagements/new" className="btn btn-primary">New engagement</Link>} />
        ) : (
          <div className="data-table" style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr><th>Talk</th><th>Speaker</th><th>Event</th><th>Status</th><th>Next due</th></tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const sp = speakers[e.speakerId];
                  const ev = events[e.eventId];
                  const overdue = e.nextDueDate && e.nextDueDate < today;
                  return (
                    <tr key={e.id}>
                      <td><Link to={`/engagements/${e.id}`}>{e.talkTitle ?? '(untitled)'}</Link></td>
                      <td>{sp ? <Link to={`/speakers/${sp.id}`}>{sp.firstName} {sp.lastName}</Link> : '—'}</td>
                      <td>{ev ? <Link to={`/events/${ev.id}/edit`}>{ev.name}</Link> : '—'}</td>
                      <td><StatusBadge status={e.status} /></td>
                      <td style={{ color: overdue ? 'var(--color-danger)' : undefined, fontSize: 'var(--text-sm)' }}>
                        {formatDate(e.nextDueDate)}
                        {(e.pendingSubmissions ?? []).some((s) => s.reviewStatus === 'pending') && (
                          <span className="badge badge-warning" style={{ marginLeft: 6 }}>Review</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
